import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Link } from 'react-router-dom';
import API_BASE_URL from '../../../config/api';
import {
    RiShieldUserLine, RiFingerprintLine, RiBriefcaseLine, RiCalendarLine,
    RiUserLine, RiCake2Line, RiHeart2Line, RiMailLine, RiPhoneLine,
    RiMapPinLine, RiTeamLine, RiGroupLine, RiUserHeartLine, RiFileTextLine,
    RiDownloadLine, RiAlertLine
} from 'react-icons/ri';
import { FaClock } from 'react-icons/fa';
import Topbar from '../mini-comp/Topbar';
import Sidebar from '../mini-comp/Sidebar';

const EmployeeDashboard = () => {
    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchEmployeeData = async () => {
            try {
                const token = Cookies.get('token');
                if (!token) {
                    setError('No authentication token found.');
                    setLoading(false);
                    return;
                }

                const response = await axios.get(`${API_BASE_URL}/api/v1/employees/`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                // The endpoint might return a list or a single object. 
                // Based on AllEmployees, it's usually a list. 
                // If it's the employee's own dashboard, we assume the first item or the specific record.
                if (Array.isArray(response.data)) {
                    setEmployee(response.data[0]); // Fallback to first employee found
                } else {
                    setEmployee(response.data);
                }
                setLoading(false);
            } catch (err) {
                console.error('Error fetching employee data:', err);
                setError('Failed to load employee data.');
                setLoading(false);
            }
        };

        fetchEmployeeData();
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const getStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case 'active': return 'bg-label-success';
            case 'terminated': return 'bg-label-danger';
            case 'suspended': return 'bg-label-warning';
            default: return 'bg-label-secondary';
        }
    };

    const getGenderLabel = (gender) => {
        switch (gender?.toLowerCase()) {
            case 'm': return 'Male';
            case 'f': return 'Female';
            default: return gender || 'N/A';
        }
    };

    if (loading) {
        return (
            <div>
                <Topbar />
                <Sidebar />
                <div className="app-content-area">
                    <div className="app-content-wrap">
                        <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !employee) {
        return (
            <div>
                <Topbar />
                <Sidebar />
                <div className="app-content-area">
                    <div className="app-content-wrap">
                        <div className="container-fluid">
                            <div className="alert alert-danger mt-4">
                                {error || 'Employee profile not found.'}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Topbar />
            <Sidebar />

            <div className="app-content-area">
                <div className="app-content-wrap">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-xxl-4 col-xl-12">
                                <div className="card">
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <h4 className="mb-0">Personal Information</h4>
                                    </div>
                                    <div className="card-body pt-15">
                                            <div className="text-center mb-10">
                                                <div className="avatar avatar-big radius-100 shadow-sm border p-1 bg-white">
                                                    <img
                                                        className="radius-100 object-fit-cover"
                                                        src={employee.photo ? (employee.photo.startsWith('http') ? employee.photo : API_BASE_URL + employee.photo) : "/assets/images/avatar/avatar-thumb-dummy.png"}
                                                        alt={employee.full_name}
                                                        style={{ width: '120px', height: '120px' }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="profile-info text-center mb-25">
                                                <div className="d-flex align-items-center justify-content-center gap-10 mb-10">
                                                    <h3 className="mb-0 fw-bold">{employee.full_name}</h3>
                                                    <span className={`badge ${getStatusBadge(employee.employment_status)} border-radius-5 fs-11 px-3`}>
                                                        {employee.employment_status?.toUpperCase()}
                                                    </span>
                                                </div>
                                                <h6 className="text-muted mb-0 fw-6 fs-15">{employee.position}</h6>
                                            </div>

                                            <div className="employee-details-content">
                                                {/* General Information Group */}
                                                <div className="info-section mb-25">
                                                    <div className="section-header d-flex align-items-center gap-10 mb-15 border-bottom pb-2">
                                                        <RiShieldUserLine className="text-primary fs-20" />
                                                        <h6 className="mb-0 text-uppercase tracking-wider fs-13 fw-bold">General Information</h6>
                                                    </div>
                                                    <div className="row g-4">
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">Employee ID</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <RiFingerprintLine className="text-primary" />
                                                                    <span className="fw-bold">{employee.employee_id}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">Category</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <RiBriefcaseLine className="text-primary" />
                                                                    <span className="fw-bold text-capitalize">{employee.category}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">Date of Join</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <RiCalendarLine className="text-primary" />
                                                                    <span className="fw-bold">{formatDate(employee.created_at)}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">Gender</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <RiUserLine className="text-primary" />
                                                                    <span className="fw-bold">{getGenderLabel(employee.gender)}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">Birthday</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <RiCake2Line className="text-primary" />
                                                                    <span className="fw-bold">{formatDate(employee.date_of_birth)}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">Marital Status</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <RiHeart2Line className="text-primary" />
                                                                    <span className="fw-bold text-capitalize">{employee.marital_status}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">Working Days/Month</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <RiCalendarLine className="text-primary" />
                                                                    <span className="fw-bold text-capitalize">{employee.working_days_per_month}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">Working Hours/Day</span>
                                                                <div className="d-flex align-items-center gap-12">
                                                                    <FaClock className="text-primary" />
                                                                    <span className="fw-bold text-capitalize">{employee.working_hours_per_day}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">Hourly Rate</span>
                                                                <div className="d-flex align-items-center gap-12">
                                                                    <FaClock className="text-primary" />
                                                                    <span className="fw-bold text-capitalize">{employee.hourly_rate}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">SSR Number</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <span className="fw-bold text-capitalize">{employee.nssa_ssr_number}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">TIN NUmber</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <span className="fw-bold text-capitalize">{employee.zimra_tin_number}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Contact Information Group */}
                                                <div className="info-section mb-25">
                                                    <div className="section-header d-flex align-items-center gap-10 mb-15 border-bottom pb-2">
                                                        <RiMailLine className="text-primary fs-20" />
                                                        <h6 className="mb-0 text-uppercase tracking-wider fs-13 fw-bold">Contact Details</h6>
                                                    </div>
                                                    <div className="row g-4">
                                                        <div className="col-md-6">
                                                            <div className="detail-item p-3 bg-light-subtle radius-10">
                                                                <span className="text-muted fs-12 d-block mb-1">Email Address</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <RiMailLine className="text-primary text-opacity-50" />
                                                                    <span className="fw-bold">{employee.email}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="detail-item p-3 bg-light-subtle radius-10">
                                                                <span className="text-muted fs-12 d-block mb-1">Phone Number</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <RiPhoneLine className="text-primary text-opacity-50" />
                                                                    <span className="fw-bold">{employee.phone_number}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-12">
                                                            <div className="detail-item p-3 bg-light-subtle radius-10 border-start-primary">
                                                                <span className="text-muted fs-12 d-block mb-1">Home Address</span>
                                                                <div className="d-flex align-items-start gap-8">
                                                                    <RiMapPinLine className="text-primary mt-1" />
                                                                    <span className="fw-6 fs-14">{employee.home_address}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Spouse & Family Group (Conditional) */}
                                                {(employee.marital_status?.toLowerCase() === 'married' || employee.marital_status === 'Married') && (
                                                    <div className="info-section mb-25">
                                                        <div className="section-header d-flex align-items-center gap-10 mb-15 border-bottom pb-2">
                                                            <RiTeamLine className="text-primary fs-20" />
                                                            <h6 className="mb-0 text-uppercase tracking-wider fs-13 fw-bold">Family Details</h6>
                                                        </div>
                                                        <div className="row g-4">
                                                            <div className="col-md-6">
                                                                <div className="detail-item">
                                                                    <span className="text-muted fs-12 d-block mb-1">Spouse Name</span>
                                                                    <span className="fw-bold">{employee.spouse_name}</span>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-6">
                                                                <div className="detail-item">
                                                                    <span className="text-muted fs-12 d-block mb-1">Spouse Contact</span>
                                                                    <span className="fw-bold">{employee.spouse_contact}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Next of Kin Group (Conditional: Hide if Married) */}
                                                {(employee.marital_status?.toLowerCase() !== 'married' && employee.marital_status !== 'Married') && (
                                                    <div className="info-section">
                                                        <div className="section-header d-flex align-items-center gap-10 mb-15 border-bottom pb-2">
                                                            <RiGroupLine className="text-primary fs-20" />
                                                            <h6 className="mb-0 text-uppercase tracking-wider fs-13 fw-bold">Next of Kin / Emergency</h6>
                                                        </div>
                                                        <div className="row g-4">
                                                            <div className="col-md-4">
                                                                <div className="detail-item">
                                                                    <span className="text-muted fs-12 d-block mb-1">Full Name</span>
                                                                    <span className="fw-bold">{employee.next_of_kin_name}</span>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-4">
                                                                <div className="detail-item">
                                                                    <span className="text-muted fs-12 d-block mb-1">Relationship</span>
                                                                    <span className="fw-bold">{employee.next_of_kin_relationship}</span>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-4">
                                                                <div className="detail-item">
                                                                    <span className="text-muted fs-12 d-block mb-1">Contact</span>
                                                                    <span className="fw-bold">{employee.next_of_kin_contact}</span>
                                                                </div>
                                                            </div>
                                                            <div className="col-12">
                                                                <div className="detail-item">
                                                                    <span className="text-muted fs-12 d-block mb-1">Emergency Address</span>
                                                                    <span className="fw-6 fs-14">{employee.next_of_kin_address}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                </div>
                            </div>

                            <div className="col-xxl-8 col-xl-12">
                                <div className="row">
                                    {/* Children Section */}
                                    <div className="col-md-6 mb-4">
                                        <div className="card h-100">
                                            <div className="card-header">
                                                <h4 className="mb-0">Children</h4>
                                            </div>
                                            <div className="card-body">
                                                {employee.children && employee.children.length > 0 ? (
                                                    employee.children.map((child, index) => (
                                                        <div className="d-flex align-items-center gap-3 mb-3 p-2 bg-light-subtle radius-10" key={index}>
                                                            <div className="btn-icon btn-sm btn-info-light fs-20">
                                                                <RiUserHeartLine />
                                                            </div>
                                                            <div>
                                                                <h6 className="mb-1">{child.name}</h6>
                                                                <span className="fs-13 text-muted">{getGenderLabel(child.gender)} • {child.age} Yrs</span>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="text-center py-4 text-muted">No children records.</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Qualifications Section */}
                                    <div className="col-md-6 mb-4">
                                        <div className="card h-100">
                                            <div className="card-header">
                                                <h4 className="mb-0">Qualifications</h4>
                                            </div>
                                            <div className="card-body">
                                                {employee.qualifications && employee.qualifications.length > 0 ? (
                                                    employee.qualifications.map((qual, index) => (
                                                        <div className="mb-3 p-2 bg-light-subtle radius-10" key={index}>
                                                            <h6 className="mb-1">{qual.name}</h6>
                                                            <div className="d-flex-between">
                                                                <span className="fs-13 text-muted">{qual.institution}</span>
                                                                <span className="fs-13 fw-bold">{qual.year_completed}</span>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="text-center py-4 text-muted">No qualifications listed.</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Contracts Section */}
                                    <div className="col-12 mb-4">
                                        <div className="card">
                                            <div className="card-header">
                                                <h4 className="mb-0">Active Contracts</h4>
                                            </div>
                                            <div className="card-body">
                                                <div className="table-responsive">
                                                    <table className="table text-nowrap">
                                                        <thead>
                                                            <tr>
                                                                <th>Type</th>
                                                                <th>Start Date</th>
                                                                <th>End Date</th>
                                                                <th>Salary</th>
                                                                <th>Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {employee.contracts && employee.contracts.length > 0 ? (
                                                                employee.contracts.map((contract) => (
                                                                    <tr key={contract.id}>
                                                                        <td className="text-capitalize">{contract.contract_type?.replace('_', ' ')}</td>
                                                                        <td>{formatDate(contract.start_date)}</td>
                                                                        <td>{contract.contract_type === 'fixed_term' ? formatDate(contract.end_date) : 'Indefinite'}</td>
                                                                        <td>{parseFloat(contract.salary).toLocaleString()}</td>
                                                                        <td>
                                                                            <span className={`badge ${contract.is_active ? 'bg-label-success' : 'bg-label-secondary'}`}>
                                                                                {contract.is_active ? 'Active' : 'Inactive'}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan="5" className="text-center py-4 text-muted">No active contracts found.</td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Documents Section */}
                                    <div className="col-md-6 mb-4">
                                        <div className="card h-100">
                                            <div className="card-header">
                                                <h4 className="mb-0">Personal Documents</h4>
                                            </div>
                                            <div className="card-body">
                                                {employee.documents && employee.documents.length > 0 ? (
                                                    employee.documents.map((doc, index) => (
                                                        <div className="d-flex-between p-2 mb-2 bg-light-subtle radius-10" key={index}>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <RiFileTextLine className="text-primary" />
                                                                <span className="fw-bold">{doc.document_type}</span>
                                                            </div>
                                                            {doc.document_file && (
                                                                <a
                                                                    href={doc.document_file.startsWith('http') ? doc.document_file : API_BASE_URL + doc.document_file}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="btn btn-sm btn-icon btn-primary-light"
                                                                >
                                                                    <RiDownloadLine />
                                                                </a>
                                                            )}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="text-center py-4 text-muted">No documents found.</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Disciplinary Section */}
                                    <div className="col-md-6 mb-4">
                                        <div className="card h-100">
                                            <div className="card-header">
                                                <h4 className="mb-0">Disciplinary Records</h4>
                                            </div>
                                            <div className="card-body">
                                                {employee.disciplinary_records && employee.disciplinary_records.length > 0 ? (
                                                    employee.disciplinary_records.map((record, index) => (
                                                        <div className="p-2 mb-2 border-start border-danger border-3 bg-light-subtle radius-10" key={index}>
                                                            <div className="d-flex-between mb-1">
                                                                <h6 className="mb-0 text-capitalize">{record.record_type?.replace('_', ' ')}</h6>
                                                                <span className="fs-12 text-muted">{formatDate(record.created_at)}</span>
                                                            </div>
                                                            <p className="fs-13 text-muted mb-0">{record.description}</p>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="text-center py-4 text-muted">No disciplinary records.</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmployeeDashboard;